/**
 * Occupancies v2 API client — powers the Acuity tab of the Regulatory
 * section. One backend row per (facility, year) carrying the bed counts
 * per care type (skilled / assisted / independent / additional).
 *
 * Always hits the real backend via `getJsonReal` — occupancies are not
 * part of the mock layer.
 *
 * Endpoints (top-level — NOT scoped under `/submissions/`):
 *   GET    /v2/occupancies/             ?submission_id=… &facility_id=… &year=…
 *   POST   /v2/occupancies/             body: OccupancyCreate
 *   PATCH  /v2/occupancies/{id}         body: OccupancyUpdate
 *   DELETE /v2/occupancies/{id}
 *
 * Unpivot-by-care-type into `AcuityRow`s lives in
 * `src/api/adapters/regulatory.ts` — this file only speaks wire.
 */
import { getJsonReal, sendJsonReal } from "../client";

const BASE = "/v2/occupancies";

/** One row per (facility, year). Bed counts are nullable. */
export interface OccupancyListItemWire {
    id: string;
    /** Nullable in practice (stage data) — the Acuity table groups by
     *  facility_id + cms_number so null-facility rows still render. */
    facility_id: string | null;
    /** Present when the backend can't resolve facility_id (parity with
     *  surveys); used as the grouping fallback. */
    cms_number?: string | null;
    facility_name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    year: number;
    skilled_beds: number | null;
    assisted_beds: number | null;
    independent_beds: number | null;
    additional_beds: number | null;
}

export interface OccupancyListResponseWire {
    total: number;
    items: OccupancyListItemWire[];
}

export interface ListOccupanciesParams {
    submission_id?: string;
    facility_id?: string;
    year?: number;
}

export function listOccupancies(params: ListOccupanciesParams = {}): Promise<OccupancyListResponseWire> {
    const qs = new URLSearchParams();
    if (params.submission_id) qs.set("submission_id", params.submission_id);
    if (params.facility_id) qs.set("facility_id", params.facility_id);
    if (params.year !== undefined) qs.set("year", String(params.year));
    const suffix = qs.toString();
    return getJsonReal<OccupancyListResponseWire>(`${BASE}/${suffix ? `?${suffix}` : ""}`);
}

/** POST body — `facility_id` + `year` required; bed counts optional. */
export interface OccupancyCreateWire {
    facility_id: string;
    year: number;
    skilled_beds?: number | null;
    assisted_beds?: number | null;
    independent_beds?: number | null;
    additional_beds?: number | null;
}

export type OccupancyUpdateWire = Partial<Omit<OccupancyCreateWire, "facility_id">>;

export function createOccupancy(payload: OccupancyCreateWire): Promise<OccupancyListItemWire> {
    return sendJsonReal<OccupancyListItemWire>(`${BASE}/`, "POST", payload);
}

export function patchOccupancy(occupancyId: string, payload: OccupancyUpdateWire): Promise<OccupancyListItemWire> {
    return sendJsonReal<OccupancyListItemWire>(`${BASE}/${encodeURIComponent(occupancyId)}`, "PATCH", payload);
}

export function deleteOccupancy(occupancyId: string): Promise<void> {
    return sendJsonReal<void>(`${BASE}/${encodeURIComponent(occupancyId)}`, "DELETE");
}
